import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useBrands } from "@/lib/brands";
import { BrandMark } from "./BrandMark";

export default function SearchBar({ autoFocus = true, size = "hero" }) {
  const navigate = useNavigate();
  const { data: brands = [] } = useBrands();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Focus on load without yanking the page scroll.
      inputRef.current.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return brands
      .filter(
        (b) =>
          b.name.toLowerCase().includes(term) ||
          b.category.toLowerCase().includes(term) ||
          (b.slug || "").includes(term)
      )
      .slice(0, 8);
  }, [q, brands]);

  useEffect(() => {
    setActive(0);
    setOpen(q.trim().length > 0);
  }, [q]);

  function go(brand) {
    if (!brand) return;
    setOpen(false);
    setQ("");
    navigate(`/brand/${brand.slug}`);
  }

  function onKeyDown(e) {
    if (!open || results.length === 0) {
      if (e.key === "Enter" && results.length) go(results[0]);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  const big = size === "hero";

  return (
    <div ref={wrapRef} className="relative w-full">
      <label htmlFor="brand-search" className="sr-only">
        Search for a brand&apos;s customer service
      </label>
      <div
        className={`flex items-center gap-3 border-2 border-ink bg-transparent transition-colors focus-within:border-accent ${
          big ? "h-16 px-5 sm:h-20 sm:px-6" : "h-14 px-4"
        }`}
      >
        <Search
          className={big ? "h-6 w-6 text-muted sm:h-7 sm:w-7" : "h-5 w-5 text-muted"}
          strokeWidth={1.75}
        />
        <input
          id="brand-search"
          ref={inputRef}
          data-testid="brand-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => q && setOpen(true)}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls="brand-search-list"
          aria-autocomplete="list"
          placeholder="Search a brand — e.g. Netflix, Coinbase, Gmail"
          className={`w-full bg-transparent font-body text-ink placeholder:text-muted/70 focus:outline-none ${
            big ? "text-lg sm:text-2xl" : "text-base"
          }`}
        />
      </div>

      {open && results.length > 0 && (
        <ul
          id="brand-search-list"
          role="listbox"
          data-testid="brand-search-results"
          className="absolute left-0 right-0 top-full z-30 mt-[-2px] max-h-[60vh] overflow-auto border-2 border-ink bg-paper"
        >
          {results.map((b, i) => (
            <li key={b.slug} role="option" aria-selected={i === active}>
              <button
                type="button"
                data-testid={`search-result-${b.slug}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(b)}
                className={`flex w-full items-center gap-3 border-b border-hair px-4 py-3 text-left transition-colors last:border-b-0 ${
                  i === active ? "bg-ink text-paper" : "hover:bg-hair/40"
                }`}
              >
                <BrandMark name={b.name} color={i === active ? "#F5F4F0" : b.color} size="sm" />
                <span className="font-serif text-base">{b.name}</span>
                <span
                  className={`ml-auto font-mono text-[11px] uppercase tracking-widest ${
                    i === active ? "text-paper/70" : "text-muted"
                  }`}
                >
                  {b.category}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && q.trim() && results.length === 0 && (
        <div className="absolute left-0 right-0 top-full z-30 mt-[-2px] border-2 border-ink bg-paper px-4 py-4 font-mono text-sm text-muted">
          No brand found. Try another name, or{" "}
          <a href="/contact" className="text-accent underline">
            ask us to add it
          </a>
          .
        </div>
      )}
    </div>
  );
}
